/**
 * Splitting a settlement figure by the rail the money is actually moving on.
 *
 * The desk merges both rails into one day list (`mergeRailUpcoming` in
 * server/stripe-settlement.js) because the question a reader asks — what
 * reaches the bank, and when — does not care which processor moves it. But a
 * single combined figure hides something that matters: the two rails are not
 * the same KIND of number. An NMI deposit is modelled from a fitted funding
 * calendar; a Stripe payout is a date and amount Stripe has stated. Printing
 * "$12,000 incoming · NMI + Stripe" tells a reader how much, but not how much
 * of it is a promise and how much is a projection.
 *
 * So every combined figure on the desk states its split.
 */

/** Rows written before the rail field existed are NMI — that desk was the only
 *  one there was, and its sources still carry no `rail`. */
const railOf = (source) => (source?.rail === 'stripe' ? 'stripe' : 'nmi');

/**
 * Sum one merged day's sources into { nmiCents, stripeCents }.
 * Signs are preserved: a debit (chargeback, negative batch) stays negative on
 * its own rail rather than quietly cancelling out the other one.
 */
export function railSplitCents(sources = []) {
  let nmiCents = 0;
  let stripeCents = 0;
  for (const source of sources || []) {
    const cents = Number(source?.amountCents) || 0;
    if (railOf(source) === 'stripe') stripeCents += cents;
    else nmiCents += cents;
  }
  return { nmiCents, stripeCents };
}

/** The same split across a list of merged days. */
export function railSplitTotals(days = []) {
  return (days || []).reduce(
    (acc, day) => {
      const { nmiCents, stripeCents } = railSplitCents(day?.sources);
      return { nmiCents: acc.nmiCents + nmiCents, stripeCents: acc.stripeCents + stripeCents };
    },
    { nmiCents: 0, stripeCents: 0 },
  );
}

/**
 * Does this day carry money on BOTH rails? Only then does a per-day split say
 * anything the rail chip has not already said.
 */
export function isMixedRailDay(day) {
  const { nmiCents, stripeCents } = railSplitCents(day?.sources);
  return nmiCents !== 0 && stripeCents !== 0;
}
